import { DEFAULT_VAT_RATE, calcPriceExclVat, calcPriceInclVat, roundMoney } from '../../utils/vat';

// Build a fresh cart line from a selected Part. The canonical `unit_price` is
// VAT-exclusive (gross for margin-scheme parts). When the part's sell price was
// ENTERED VAT-inclusive, the exact stored gross rides along so display and the
// resulting invoice match to the cent (backend treats it as source of truth).
export function lineFromPart(part, defaultDiscount = '0') {
  const isMargin = !!part.is_margin_vat_scheme;
  // Prefer the server-resolved walk-in price (sell price OR margin over cost,
  // exact stored gross for VAT-entered prices) — same resolution the backend
  // line factory applies, so the till never underprices margin-configured parts.
  const pp = part.pos_prices;
  const fallbackPrice = part.sell_price != null ? String(part.sell_price) : part.price != null ? String(part.price) : '0';
  const enteredWithVat = pp
    ? !!pp.price_entered_with_vat && !isMargin
    : !isMargin && part.sell_price != null && !!part.sell_price_entered_with_vat && part.stored_sell_price_incl_vat != null;
  return {
    key: `p${part.id}-${new Date().getTime()}`,
    part_id: part.id,
    description: part.name || '',
    part_code: part.primary_code || part.manufacturer_code || '',
    quantity: '1',
    unit_price: pp ? String(pp.unit_price) : fallbackPrice,
    unit_price_incl_vat: isMargin
      ? String(pp ? pp.unit_price : fallbackPrice)
      : enteredWithVat
        ? String(pp ? pp.unit_price_incl_vat : part.stored_sell_price_incl_vat)
        : null,
    price_entered_with_vat: enteredWithVat,
    vat_rate: part.vat_rate != null ? part.vat_rate : 25.5,
    is_margin_vat_scheme: isMargin,
    discount_percent: defaultDiscount,
    // Unit cost for the kate readout. `price` IS the purchase price on Part, and
    // the API strips it for users without costs.view_purchase_prices — so an
    // undefined cost here means "not allowed to see it", not "free".
    cost_price: part.price != null ? String(part.price) : null,
    margin_vat_purchase_price: isMargin && part.margin_vat_purchase_price != null
      ? String(part.margin_vat_purchase_price)
      : null,
  };
}

// Build a fresh cart line from a saved job (Työlajit). Hourly jobs sell by the
// hour (quantity = hours, unit "h"); fixed/variable jobs by the piece. Job lines
// always bill at the standard VAT rate (the backend ignores Job.vat_rate, like
// work-order labor lines), so the cart uses DEFAULT_VAT_RATE to match the invoice.
// When the job was entered VAT-inclusive the exact stored gross rides along.
export function lineFromJob(job, defaultDiscount = '0') {
  const enteredWithVat = !!job.price_entered_with_vat && job.stored_price_incl_vat != null;
  return {
    key: `j${job.id}-${new Date().getTime()}`,
    part_id: null,
    job_id: job.id,
    description: job.name || '',
    part_code: job.job_code || '',
    quantity: '1',
    unit: job.price_type === 'hourly' ? 'h' : 'kpl',
    price_type: job.price_type || 'fixed',
    unit_price: job.price != null ? String(job.price) : '0',
    unit_price_incl_vat: enteredWithVat ? String(job.stored_price_incl_vat) : null,
    price_entered_with_vat: enteredWithVat,
    vat_rate: DEFAULT_VAT_RATE,
    is_margin_vat_scheme: false,
    discount_percent: defaultDiscount,
    cost_price: job.cost_price != null ? String(job.cost_price) : null,
  };
}

// A job with no usable price (variable-priced, or 0 €) must be priced by the
// seller before it lands in the cart — a silent 0 € labor line is a revenue leak.
export function jobNeedsPriceEntry(line) {
  if (!line.job_id) return false;
  if (line.price_type === 'variable') return true;
  return !(parseFloat(line.unit_price) > 0 || parseFloat(line.unit_price_incl_vat) > 0);
}

// Build a cart line from a quick-pick button (price is server-resolved, so the
// button, the cart and the invoice all show the same number). A pick is either
// a part (kind "part") or a saved job (kind "job").
export function lineFromQuickPick(pick, defaultDiscount = '0') {
  const isJob = pick.kind === 'job';
  return {
    key: `${isJob ? 'qj' : 'q'}${isJob ? pick.job_id : pick.part_id}-${new Date().getTime()}`,
    part_id: isJob ? null : pick.part_id,
    job_id: isJob ? pick.job_id : null,
    description: (isJob ? pick.job_name : pick.part_name) || '',
    part_code: (isJob ? pick.job_code : pick.part_code) || '',
    quantity: '1',
    unit: pick.unit || 'kpl',
    ...(isJob ? { price_type: pick.unit === 'h' ? 'hourly' : 'fixed' } : {}),
    unit_price: pick.unit_price != null ? String(pick.unit_price) : '0',
    unit_price_incl_vat: pick.unit_price_incl_vat != null ? String(pick.unit_price_incl_vat) : null,
    price_entered_with_vat: !!pick.price_entered_with_vat,
    vat_rate: isJob ? DEFAULT_VAT_RATE : pick.vat_rate != null ? parseFloat(pick.vat_rate) : 25.5,
    is_margin_vat_scheme: isJob ? false : !!pick.is_margin_vat_scheme,
    discount_percent: defaultDiscount,
    cost_price: pick.cost_price != null ? String(pick.cost_price) : null,
  };
}

export function emptyFreeTextLine(enteredWithVat = false, defaultDiscount = '0') {
  return {
    key: `f-${new Date().getTime()}`,
    part_id: null,
    job_id: null,
    unit: 'kpl',
    description: '',
    part_code: '',
    quantity: '1',
    unit_price: '0',
    unit_price_incl_vat: enteredWithVat ? '0' : null,
    price_entered_with_vat: enteredWithVat,
    vat_rate: 25.5,
    is_margin_vat_scheme: false,
    discount_percent: defaultDiscount,
  };
}

// The unit price to DISPLAY for a cart line, following the org's
// default_price_includes_vat setting. Margin-scheme prices are always gross.
// Returns { value, inclVat } so callers can render the sis. alv / alv 0 % tag.
export function displayUnitPrice(line, showInclVat) {
  const excl = parseFloat(line.unit_price) || 0;
  if (line.is_margin_vat_scheme) {
    return { value: excl, inclVat: true }; // unit_price is the gross customer price
  }
  if (showInclVat) {
    const incl = line.price_entered_with_vat && line.unit_price_incl_vat != null
      ? parseFloat(line.unit_price_incl_vat) || 0
      : calcPriceInclVat(excl, line.vat_rate);
    return { value: incl, inclVat: true };
  }
  return { value: excl, inclVat: false };
}

// Display prices for a SERVER-side sale line (lähete detail / koontilasku
// preview) per the org's VAT display setting. Margin-scheme lines are always
// gross; VAT-inclusive-entered prices use the exact stored gross.
export function serverLineDisplay(line, showInclVat) {
  if (line.is_margin_vat_scheme) {
    return {
      unit: parseFloat(line.unit_price_incl_vat || line.unit_price) || 0,
      total: parseFloat(line.line_total_incl_vat) || 0,
      inclVat: true,
    };
  }
  if (showInclVat) {
    const unit = line.price_entered_with_vat && line.unit_price_incl_vat != null
      ? parseFloat(line.unit_price_incl_vat) || 0
      : calcPriceInclVat(parseFloat(line.effective_price) || 0, parseFloat(line.vat_rate) || 0);
    return { unit, total: parseFloat(line.line_total_incl_vat) || 0, inclVat: true };
  }
  return {
    unit: parseFloat(line.effective_price) || 0,
    total: parseFloat(line.line_total_excl_vat) || 0,
    inclVat: false,
  };
}

// Per-line estimate mirroring verstaani/utils/vat.py calculate_line_totals
// (same three paths, same per-line rounding) so the till's numbers equal the
// invoice's numbers. Margin lines contribute gross only to the totals; the margin
// VAT is returned alongside for the kate readout, which must not count it as income.
export function estimateLine(line) {
  const qty = parseFloat(line.quantity) || 0;
  const disc = parseFloat(line.discount_percent) || 0;
  const factor = 1 - disc / 100;
  const rate = parseFloat(line.vat_rate) || 0;

  if (line.is_margin_vat_scheme) {
    const incl = roundMoney((parseFloat(line.unit_price) || 0) * qty * factor);
    // VAT on the margin only (AVL voittomarginaalijärjestelmä), mirroring
    // verstaani/utils/vat.py::compute_margin_vat. Not part of the totals — those
    // are gross for margin rows — but the kate readout has to subtract it, since
    // that VAT is owed to the state and is not the workshop's money.
    const purchaseGross = roundMoney((parseFloat(line.margin_vat_purchase_price) || 0) * qty);
    const grossMargin = incl - purchaseGross;
    const vatMult = rate / 100;
    const marginVat = incl === 0 || grossMargin <= 0
      ? 0
      : roundMoney((grossMargin * vatMult) / (1 + vatMult));
    return { excl: null, vat: null, incl, margin: true, marginVat };
  }
  if (line.price_entered_with_vat && line.unit_price_incl_vat != null) {
    const incl = roundMoney((parseFloat(line.unit_price_incl_vat) || 0) * qty * factor);
    const excl = roundMoney(incl / (1 + rate / 100));
    return { excl, vat: roundMoney(incl - excl), incl, margin: false };
  }
  const excl = roundMoney((parseFloat(line.unit_price) || 0) * qty * factor);
  const vat = roundMoney((excl * rate) / 100);
  return { excl, vat, incl: roundMoney(excl + vat), margin: false };
}

export function estimateLineInclVat(line) {
  return estimateLine(line).incl;
}

// What a line discount actually costs, in euros. Mirrors the backend
// PartsSaleLine.line_discount_amount: the undiscounted total minus the charged
// one, through the same per-line rounding, so the figure on screen is exactly
// the money that left the till and never a re-derived estimate.
//
// `line` may be a CART line (client-side, estimateLine math) or a SERVER line
// (which carries the amounts already computed). Returns null when there is no
// discount, so callers can skip the whole readout with one check.
export function lineDiscountAmount(line, showInclVat) {
  const pct = parseFloat(line.discount_percent) || 0;
  if (pct <= 0) return null;
  const gross = !!line.is_margin_vat_scheme || !!showInclVat;
  if (line.line_discount_amount != null || line.line_discount_amount_incl_vat != null) {
    const server = gross ? line.line_discount_amount_incl_vat : line.line_discount_amount;
    const value = parseFloat(server);
    return Number.isFinite(value) && value !== 0 ? value : null;
  }
  const charged = estimateLine(line);
  const full = estimateLine({ ...line, discount_percent: '0' });
  if (charged.margin) return roundMoney(full.incl - charged.incl);
  return gross ? roundMoney(full.incl - charged.incl) : roundMoney(full.excl - charged.excl);
}

// Total of every line discount on a cart / sale, in the displayed VAT basis.
export function totalDiscountAmount(lines, showInclVat) {
  return roundMoney(
    (lines || []).reduce((sum, l) => sum + (lineDiscountAmount(l, showInclVat) || 0), 0)
  );
}

// Kate (gross margin) for one line, AFTER the line discount — the number the
// counter actually needs before agreeing to an "ale 30 %".
//
// Margin-VAT rows follow the same rule as the work-order margin panel
// (MaintenanceProcessView): cost is the gross purchase price and revenue is the
// gross customer price minus the VAT owed on the margin, so "myynti − ostohinta
// = kate" holds and the two screens agree.
//
// `hasCost` false means the cost is unknown or hidden by permission; callers must
// say so rather than render a flattering 100 %.
export function lineMargin(line) {
  const est = estimateLine(line);
  const rawCost = line.is_margin_vat_scheme ? line.margin_vat_purchase_price : line.cost_price;
  const unitCost = parseFloat(rawCost);
  if (!Number.isFinite(unitCost) || unitCost <= 0) {
    return { amount: null, percent: null, unitCost: null, hasCost: false };
  }
  const qty = parseFloat(line.quantity) || 0;
  const cost = roundMoney(unitCost * qty);
  const revenue = est.margin ? roundMoney(est.incl - (est.marginVat || 0)) : est.excl;
  const amount = roundMoney(revenue - cost);
  return {
    amount,
    percent: revenue > 0 ? (amount / revenue) * 100 : null,
    unitCost,
    hasCost: true,
  };
}

// Kate for a whole cart / sale. Lines with no known cost are excluded from both
// sides of the ratio and reported separately, so one unpriced row cannot silently
// inflate the total.
export function totalMargin(lines) {
  let amount = 0;
  let revenue = 0;
  let missingCost = 0;
  (lines || []).forEach((l) => {
    const m = lineMargin(l);
    if (!m.hasCost) {
      missingCost += 1;
      return;
    }
    const est = estimateLine(l);
    amount += m.amount;
    revenue += est.margin ? roundMoney(est.incl - (est.marginVat || 0)) : est.excl;
  });
  amount = roundMoney(amount);
  revenue = roundMoney(revenue);
  return {
    amount,
    percent: revenue > 0 ? (amount / revenue) * 100 : null,
    missingCost,
    hasAny: revenue > 0 || amount !== 0,
  };
}

export function estimateTotalInclVat(lines) {
  return roundMoney((lines || []).reduce((sum, l) => sum + estimateLine(l).incl, 0));
}

// Full client-side cart summary: standard net + VAT, margin-scheme gross, total.
// Sums the per-line rounded values exactly like Invoice.recalculate_totals.
export function estimateCartTotals(lines) {
  let net = 0;
  let vat = 0;
  let marginGross = 0;
  (lines || []).forEach((l) => {
    const est = estimateLine(l);
    if (est.margin) {
      marginGross += est.incl;
    } else {
      net += est.excl;
      vat += est.vat;
    }
  });
  net = roundMoney(net);
  vat = roundMoney(vat);
  marginGross = roundMoney(marginGross);
  return { net, vat, marginGross, total: roundMoney(net + vat + marginGross) };
}

// Map a cart line to the API line-spec shape expected by the parts-sales endpoints.
export function toLineSpec(line) {
  const spec = {
    part_id: line.part_id || null,
    // Only job lines carry job_id — part/free payloads stay byte-identical.
    ...(line.job_id ? { job_id: line.job_id } : {}),
    quantity: line.quantity || '1',
    unit_price: line.unit_price,
    discount_percent: line.discount_percent || '0',
    description: line.description || '',
    // Kohde only when set, so payloads of lines without one stay byte-identical.
    ...(line.target_label ? { target_label: line.target_label } : {}),
  };
  if (line.is_margin_vat_scheme && !line.part_id && !line.job_id) {
    spec.is_margin_vat_scheme = true;
    if (line.margin_vat_purchase_price) spec.margin_vat_purchase_price = line.margin_vat_purchase_price;
  }
  if (!line.is_margin_vat_scheme && line.price_entered_with_vat && line.unit_price_incl_vat != null) {
    spec.unit_price_incl_vat = line.unit_price_incl_vat;
    spec.price_entered_with_vat = true;
  }
  return spec;
}

// Ensure a line's excl/incl pair stays coherent after a price edit.
// `enteredValue` is in the mode indicated by `withVat`.
export function applyPriceEntry(line, enteredValue, withVat) {
  const rate = parseFloat(line.vat_rate) || 0;
  const numeric = parseFloat(enteredValue) || 0;
  if (line.is_margin_vat_scheme) {
    return { ...line, unit_price: String(enteredValue), unit_price_incl_vat: String(enteredValue), price_entered_with_vat: true };
  }
  if (withVat) {
    return {
      ...line,
      unit_price: String(roundMoney(calcPriceExclVat(numeric, rate))),
      unit_price_incl_vat: String(enteredValue),
      price_entered_with_vat: true,
    };
  }
  return { ...line, unit_price: String(enteredValue), unit_price_incl_vat: null, price_entered_with_vat: false };
}

// Translate a refused parts-sale API call. The backend returns a stable
// `error_code` (see parts_sales/exceptions.py) plus an English `error` detail
// meant for logs — never render that detail, or a Finnish seller gets an
// English snackbar. Unknown/absent codes fall back to the caller's own
// localized message.
export function partsSaleErrorMessage(apiError, t, fallbackKey) {
  const code = apiError?.response?.data?.error_code;
  if (code) {
    const key = `parts_sales.errors.${code}`;
    const translated = t(key);
    // i18next echoes the key back when it has no entry for it.
    if (translated && translated !== key) return translated;
  }
  return t(fallbackKey);
}
